import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExclamationTriangle, faCheckCircle, faTimes, faCalendarAlt,
} from '@fortawesome/free-solid-svg-icons';
import api from '../../../Api';
import { useModalDismiss } from '../../../hooks/useModalDismiss';

const STEP_LABELS = {
  ao: 'Avant-Vente', setup: 'Setup', terrain: 'Terrain', reporting: 'Reporting', closing: 'Closing',
};

function formatDeadline(date) {
  if (!date) return null;
  return new Date(date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

const StepWarningModal = ({ isOpen, onClose, onConfirm, project, fromStep, toStep, loading }) => {
  const [warnings, setWarnings] = useState([]);
  const [loadingWarnings, setLoadingWarnings] = useState(true);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!isOpen || !project?.id || !toStep) return;
    const fetch = async () => {
      setLoadingWarnings(true);
      try {
        const res = await api.get(`/projects/${project.id}/transition-warnings`, { params: { to_step: toStep } });
        setWarnings(res.data.data?.warnings || []);
      } catch { setWarnings([]); }
      finally { setLoadingWarnings(false); }
    };
    fetch();
  }, [isOpen, project?.id, toStep]);

  const handleClose = () => { setNotes(''); onClose(); };
  // Carries a comment field: only Escape and the explicit buttons dismiss it.
  const dismiss = useModalDismiss(handleClose, { backdropClose: false, enabled: isOpen && !!project });

  if (!isOpen || !project) return null;

  const toDeadline = project[`${toStep}_deadline`];

  const handleConfirm = () => {
    const acknowledged = warnings.map(w => ({
      slot_name: w.slot_name,
      survey_title: w.survey_title,
      vague_label: w.vague_label,
      type: w.type,
      checks: w.checks?.map(c => c.message) || [w.message],
    }));
    onConfirm({ notes: notes.trim() || null, warnings_acknowledged: acknowledged });
    setNotes('');
  };
  const canConfirm = !loading && !loadingWarnings;

  return (
    <div style={overlay} {...dismiss}>
      <div style={modal} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={header}>
          <span style={{ fontSize: '0.9rem', fontWeight: 700, color: '#111827' }}>
            Déplacer vers {STEP_LABELS[toStep]}
          </span>
          <button onClick={handleClose} disabled={loading} style={closeBtn}><FontAwesomeIcon icon={faTimes} /></button>
        </div>

        {/* Transition line + deadline */}
        <div style={{ padding: '12px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            <div style={pillInactive}>{STEP_LABELS[fromStep]}</div>
            <div style={{ width: 24, height: 1, background: '#d1d5db' }} />
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#dc2626' }} />
            <div style={{ width: 24, height: 1, background: '#dc2626' }} />
            <div style={pillActive}>{STEP_LABELS[toStep]}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {toStep !== 'closing' && toDeadline && (
              <div style={{ fontSize: '0.72rem', color: '#6b7280', display: 'flex', alignItems: 'center', gap: 4 }}>
                <FontAwesomeIcon icon={faCalendarAlt} size="xs" style={{ opacity: 0.5 }} />
                {formatDeadline(toDeadline)}
              </div>
            )}
            <span style={{ fontSize: '0.72rem', color: '#9ca3af' }}>{project.name}</span>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: 20, overflowY: 'auto', maxHeight: 'calc(85vh - 200px)' }}>
          {loadingWarnings ? (
            <div style={{ textAlign: 'center', padding: '30px 0' }}>
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600" />
              <div style={{ fontSize: '0.78rem', color: '#9ca3af', marginTop: 8 }}>Vérification...</div>
            </div>
          ) : warnings.length === 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8 }}>
              <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#16a34a' }} />
              <span style={{ fontSize: '0.82rem', color: '#166534' }}>Toutes les conditions sont remplies.</span>
            </div>
          ) : (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, marginBottom: 12, fontSize: '0.78rem', color: '#92400e' }}>
                <FontAwesomeIcon icon={faExclamationTriangle} />
                {warnings.length} avertissement(s). Vous pouvez continuer malgré tout.
              </div>

              {warnings.map((w, i) => (
                <div key={i} style={{ border: '1px solid #f3f4f6', borderRadius: 8, padding: '10px 14px', marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#111827' }}>{w.slot_name}</span>
                    {w.survey_type && (
                      <span style={{ fontSize: '0.58rem', fontWeight: 600, padding: '1px 6px', borderRadius: 6, background: '#f3f4f6', color: '#374151' }}>
                        {w.survey_type}
                      </span>
                    )}
                  </div>
                  {/* A slot can cover several surveys, so each warning names the one it
                      came from — otherwise identical slot rows are indistinguishable. */}
                  {w.survey_title && (
                    <div style={{ fontSize: '0.68rem', color: '#9ca3af', marginBottom: 4 }}>
                      {w.survey_title}
                      {w.vague_label && <span style={{ color: '#7c3aed' }}> · {w.vague_label}</span>}
                    </div>
                  )}
                  {w.type === 'unlinked' ? (
                    <div style={{ fontSize: '0.75rem', color: '#d97706', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#d97706' }} />
                      {w.message}
                    </div>
                  ) : (w.checks || []).map((c, ci) => (
                    <div key={ci} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0', fontSize: '0.75rem', color: '#374151' }}>
                      <div style={{ width: 5, height: 5, borderRadius: '50%', background: c.severity === 'danger' ? '#ef4444' : '#f59e0b', flexShrink: 0 }} />
                      {c.message}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}

          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151', marginBottom: 5 }}>Commentaire</div>
            <textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optionnel..."
              style={{ width: '100%', fontSize: '0.82rem', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, outline: 'none', resize: 'vertical', transition: 'border-color 0.15s' }}
              onFocus={e => e.target.style.borderColor = '#dc2626'} onBlur={e => e.target.style.borderColor = '#d1d5db'} />
          </div>
        </div>

        {/* Footer */}
        <div style={footerStyle}>
          <button onClick={handleClose} disabled={loading} style={cancelBtnStyle}>Annuler</button>
          <button onClick={handleConfirm} disabled={!canConfirm}
            style={{ ...confirmBtnStyle, background: canConfirm ? '#dc2626' : '#e5e7eb', color: canConfirm ? '#fff' : '#9ca3af', cursor: canConfirm ? 'pointer' : 'default' }}>
            {loading ? 'En cours...' : warnings.length > 0 ? 'Continuer' : 'Confirmer'}
          </button>
        </div>
      </div>
      <style>{`@keyframes warningModalIn { from { opacity: 0; transform: translateY(10px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }`}</style>
    </div>
  );
};

const overlay = { position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.4)' };
const modal = { background: '#fff', borderRadius: 12, width: '100%', maxWidth: 540, maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.15)', overflow: 'hidden', animation: 'warningModalIn 0.2s ease' };
const header = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid #f3f4f6', flexShrink: 0 };
const closeBtn = { background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', padding: 4 };
const footerStyle = { display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '14px 20px', borderTop: '1px solid #f3f4f6', flexShrink: 0 };
const cancelBtnStyle = { padding: '7px 16px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: '0.78rem', fontWeight: 500, cursor: 'pointer' };
const confirmBtnStyle = { padding: '7px 16px', borderRadius: 6, border: 'none', fontSize: '0.78rem', fontWeight: 600 };
const pillInactive = { fontSize: '0.72rem', fontWeight: 600, padding: '5px 14px', borderRadius: 20, background: '#f3f4f6', color: '#6b7280', whiteSpace: 'nowrap' };
const pillActive = { fontSize: '0.72rem', fontWeight: 600, padding: '5px 14px', borderRadius: 20, background: '#111827', color: '#fff', whiteSpace: 'nowrap' };

export default StepWarningModal;
